import React, { useMemo } from 'react';
import { Group } from '@visx/group';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { LinePath } from '@visx/shape';
import { RectClipPath } from '@visx/clip-path';

// Initialize some variables
const axisColor = '#000';
const axisBottomTickLabelProps = {
  textAnchor: 'middle',
  fontFamily: 'Arial',
  fontSize: 15,
  fill: axisColor,
};
const axisLeftTickLabelProps = {
  dx: '-0.25em',
  dy: '0.25em',
  fontFamily: 'Arial',
  fontSize: 15,
  textAnchor: 'end',
  fill: axisColor,
};

export default function PlotGraph({
  id,
  data,
  gradientColor,
  width,
  yMax,
  margin,
  xScale,
  yScale,
  hideBottomAxis = false,
  hideLeftAxis = false,
  top,
  left,
  children,
  traceData,
  displaySettingJson,
}) {
  // 事業者ごとの塗り分け
  const frequencyBands = useMemo(() => {
    const { band_label } = displaySettingJson;

    let list = [];

    if (band_label) {
      list = [
        { start: 3400, end: 3440, color: band_label["3400-3440"].color, label: band_label["3400-3440"].name },
        { start: 3440, end: 3480, color: band_label["3440-3480"].color, label: band_label["3440-3480"].name },
        { start: 3480, end: 3520, color: band_label["3480-3520"].color, label: band_label["3480-3520"].name },
        { start: 3520, end: 3560, color: band_label["3520-3560"].color, label: band_label["3520-3560"].name },
        { start: 3560, end: 3600, color: band_label["3560-3600"].color, label: band_label["3560-3600"].name },
        { start: 3600, end: 3700, color: band_label["3600-3700"].color, label: band_label["3600-3700"].name },
        { start: 3700, end: 3800, color: band_label["3700-3800"].color, label: band_label["3700-3800"].name },
        { start: 3800, end: 3900, color: band_label["3800-3900"].color, label: band_label["3800-3900"].name },
        { start: 3900, end: 4000, color: band_label["3900-4000"].color, label: band_label["3900-4000"].name },
        { start: 4000, end: 4100, color: band_label["4000-4100"].color, label: band_label["4000-4100"].name },
      ]
    }

    return list;
  }, [displaySettingJson]);

  if (width < 10) return null;
  return (
    <Group left={left || margin.left} top={top || margin.top}>
      <RectClipPath id={`chart-clip-${id}`} width={width - margin.right - margin.left} height={yMax} />
      {frequencyBands.map((band, i) => {
        const x = xScale(band.start);
        const width = xScale(band.end) - x;
        return (
          <g key={i} clipPath={`url(#chart-clip-${id})`}>
            <rect
              x={x}
              y={0}
              width={width}
              height={yMax}
              fill={band.color}
              fillOpacity={0.5}
            />
            <text
              x={x + width / 2}
              y={20}
              fontSize={15}
              fill="#000"
              textAnchor="middle"
            >
              {band.label}
            </text>
          </g>
        );
      })}
      <LinePath
        data={traceData.max}
        stroke="red"
        strokeWidth={1.5}
        x={(d) => xScale(d.x)}
        y={(d) => yScale(d.y)}
        clipPath={`url(#chart-clip-${id})`}
      />
      <LinePath
        data={traceData["20%"]}
        stroke="green"
        strokeWidth={1.5}
        x={(d) => xScale(d.x)}
        y={(d) => yScale(d.y)}
        clipPath={`url(#chart-clip-${id})`}
      />
      <LinePath
        data={traceData.ave}
        stroke="blue"
        strokeWidth={1.5}
        x={(d) => xScale(d.x)}
        y={(d) => yScale(d.y)}
        clipPath={`url(#chart-clip-${id})`}
      />
      {!hideBottomAxis && (
        <AxisBottom
          top={yMax}
          scale={xScale}
          numTicks={width > 520 ? 10 : 5}
          stroke={axisColor}
          tickStroke={axisColor}
          tickLabelProps={axisBottomTickLabelProps}
          label="MHz"
          labelProps={axisBottomTickLabelProps}
        />
      )}
      {!hideLeftAxis && (
        <AxisLeft
          scale={yScale}
          numTicks={10}
          stroke={axisColor}
          tickStroke={axisColor}
          tickLabelProps={axisLeftTickLabelProps}
          label="dBm/MHz"
          labelProps={{
            ...axisLeftTickLabelProps,
            textAnchor: 'middle',
            dx: '-0.5em',
            dy: '-0.5em',
          }}
        />
      )}
      {children}
    </Group>
  );
}
